import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '../../../../shared/components/Alert/Alert';
import Input from '../../../../shared/components/Input/Input';
import Loader from '../../../../shared/components/Loader/Loader';
import { convertAffair, getAffairContacts, getAffairNodes, getAffairPlanning, getAffairPlanningCatalog, getAffairQuotes } from '../affair.api';
import { formatAffairAmount, getAffairPartnerName } from '../affairFormatters';
import { AFFAIR_PROJECT_TYPES } from '../affairModel';
import { hasValidationErrors, validateInterventionConversion, validateProjectConversion } from '../affairValidation';
import './AffairConversionWizard.scss';

const STEPS = ['Vérification commerciale', 'Projet à créer', 'Lots et phases', 'Structure du projet', 'LDA et GED', 'Résumé'];
const LOTS = ['CFO', 'CFA', 'SSI', 'PV', 'IRVE', 'CVC', 'Autre'];
const PHASES = ['Chiffrage', 'Conception', 'EXE', 'DOE'];
const MANAGEMENT_OPTIONS = [['reunionChantier', 'Réunion de chantier'], ['reunionSynthese', 'Réunion de synthèse'], ['pointsClients', 'Entretien client']];
const FALLBACK_DELIVERABLE_OPTIONS = [
  { code: 'IMP', label: 'Plan d’implantation' },
  { code: 'RES', label: 'Plan de réservations' },
  { code: 'CIR', label: 'Plan de circuits' },
  { code: 'CHE', label: 'Plan de cheminements' },
  { code: 'NDC', label: 'Note de calcul BT' },
  { code: 'FTE', label: 'Fiches techniques' },
];

function ProjectConversionWizard({ affair, onConverted }) {
  const [step, setStep] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [deliverableOptions, setDeliverableOptions] = useState(FALLBACK_DELIVERABLE_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState('');
  const [convertedResult, setConvertedResult] = useState(null);
  const [form, setForm] = useState({
    targetType: 'project',
    acceptedQuoteId: '',
    primaryContactId: '',
    project: {
      name: affair.name,
      label: affair.label || '',
      description: affair.description || '',
      startDate: affair.desiredStartDate || '',
      endDate: affair.desiredEndDate || '',
      projectType: affair.projectType || '',
      buildingType: affair.buildingType || '',
      surface: affair.surface ?? '',
      workType: affair.workType || '',
    },
    treeMode: 'simple',
    nodes: [],
    lots: [],
    phases: {},
    management: {},
    // La GED projet est créée par défaut à partir du périmètre Lots / Phases.
    // L'arborescence LDA reste indépendante et ne pilote pas la GED.
    ldaGed: { generateLda: false, createGed: true, documentTypes: {} },
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [quoteRows, contactRows, deliverableCatalog, planning, affairNodes] = await Promise.all([
        getAffairQuotes(affair.id),
        getAffairContacts(affair.id),
        getAffairPlanningCatalog(affair.id),
        getAffairPlanning(affair.id),
        getAffairNodes(affair.id),
      ]);
      const accepted = (quoteRows || []).filter((quote) => quote.status === 'accepted');
      const primary = (contactRows || []).find((contact) => contact.isPrimary || contact.is_primary);
      const plannedWork = (planning?.items || []).filter((item) => item.itemType === 'work' && item.source === 'icc' && item.documentTypeCode);
      const plannedLots = [...new Set(plannedWork.map((item) => item.lotCode).filter(Boolean))];
      const phaseLabel = (code) => ({ CON: 'Conception', EXE: 'EXE', DOE: 'DOE' }[String(code || '').toUpperCase()] || code);
      const plannedPhases = Object.fromEntries([...new Set(plannedWork.map((item) => phaseLabel(item.phaseCode)).filter(Boolean))].map((phase) => [phase, true]));
      const plannedDocuments = Object.fromEntries([...new Set(plannedWork.map((item) => item.documentTypeCode).filter(Boolean))].map((code) => [code, true]));
      setQuotes(accepted);
      setDeliverableOptions((deliverableCatalog?.deliverables || []).filter((row) => row.ldaEnabled !== false));
      setForm((current) => ({
        ...current,
        acceptedQuoteId: accepted[0]?.id || '',
        primaryContactId: primary?.contactId || primary?.contact_id || '',
        lots: plannedLots.length ? plannedLots : current.lots,
        nodes: affairNodes || [],
        phases: Object.keys(plannedPhases).length ? plannedPhases : current.phases,
        project: {
          ...current.project,
          startDate: current.project.startDate || planning?.desiredStartDate || '',
          endDate: current.project.endDate || planning?.desiredEndDate || '',
        },
        ldaGed: {
          ...current.ldaGed,
          generateLda: plannedWork.length ? true : current.ldaGed.generateLda,
          documentTypes: Object.keys(plannedDocuments).length ? plannedDocuments : current.ldaGed.documentTypes,
          planningSource: plannedWork.length > 0,
          planningWorkItemCount: plannedWork.length,
          planningEstimatedHours: Number(planning?.totalEstimatedHours || 0),
        },
      }));
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [affair.id]);

  useEffect(() => { load(); }, [load]);

  const acceptedQuote = useMemo(
    () => quotes.find((quote) => String(quote.id) === String(form.acceptedQuoteId)),
    [form.acceptedQuoteId, quotes],
  );
  const selectedPhases = useMemo(
    () => PHASES.filter((phase) => Boolean(form.phases[phase])),
    [form.phases],
  );
  const selectedDeliverables = deliverableOptions.filter((row) => form.ldaGed.documentTypes[row.code]);
  const errors = validateProjectConversion(affair, form);

  const updateProject = (event) => setForm((current) => ({
    ...current,
    project: { ...current.project, [event.target.name]: event.target.value },
  }));

  const toggle = (group, key) => setForm((current) => {
    if (group === 'lots') {
      const lots = current.lots.includes(key) ? current.lots.filter((item) => item !== key) : [...current.lots, key];
      return {
        ...current,
        lots,
        nodes: current.nodes.map((node) => ({ ...node, lots: (node.lots || []).filter((lot) => lots.includes(lot)) })),
      };
    }
    if (group === 'phases') {
      const phases = { ...current.phases, [key]: !current.phases[key] };
      const activePhases = new Set(PHASES.filter((phase) => Boolean(phases[phase])));
      return {
        ...current,
        phases,
        nodes: current.nodes.map((node) => ({ ...node, phases: (node.phases || []).filter((phase) => activePhases.has(phase)) })),
      };
    }
    return { ...current, [group]: { ...current[group], [key]: !current[group][key] } };
  });

  const toggleDeliverable = (key) => setForm((current) => ({
    ...current,
    ldaGed: {
      ...current.ldaGed,
      documentTypes: {
        ...current.ldaGed.documentTypes,
        [key]: !current.ldaGed.documentTypes[key],
      },
    },
  }));

  const next = () => {
    if (step === 4 && form.ldaGed.generateLda && selectedDeliverables.length === 0) {
      setError('Sélectionnez au moins un type de livrable pour générer la LDA.');
      return;
    }
    setError('');
    setStep((current) => current + 1);
  };

  const convert = async () => {
    if (hasValidationErrors(errors)) {
      setError(Object.values(errors)[0]);
      return;
    }
    try {
      setConverting(true);
      const result = await convertAffair(affair.id, form);
      if (result.initialization?.gedError) {
        setConvertedResult(result);
        setError(`Le projet a été créé, mais le dossier Nextcloud n’a pas pu être créé : ${result.initialization.gedError}`);
      } else {
        onConverted(result);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setConverting(false);
    }
  };

  if (loading) return <Loader label="Vérification des prérequis..." />;

  const renderStep = () => {
    if (step === 0) {
      return <div className="affair-conversion-checks">{[
        ['Affaire gagnée', affair.stage === 'won'],
        ['Partenaire renseigné', Boolean(affair.companyId)],
        ['Devis accepté', Boolean(acceptedQuote)],
        ['Date d’acceptation', Boolean(acceptedQuote?.acceptedAt || acceptedQuote?.accepted_at)],
        ['Contact principal', Boolean(form.primaryContactId)],
      ].map(([label, ok]) => <div className={ok ? 'ok' : 'ko'} key={label}><span>{ok ? '✓' : '!'}</span>{label}</div>)}</div>;
    }

    if (step === 1) {
      return <div className="affair-conversion-grid">
        <Input required label="Nom du projet" name="name" value={form.project.name} onChange={updateProject} />
        <Input label="Libellé" name="label" value={form.project.label} onChange={updateProject} />
        <Input multiline rows={3} label="Objet" name="description" value={form.project.description} onChange={updateProject} />
        <Input type="date" label="Début" name="startDate" value={form.project.startDate} onChange={updateProject} />
        <Input type="date" label="Fin" name="endDate" value={form.project.endDate} onChange={updateProject} />
        <div className="custom-input">
          <label className="custom-input__label" htmlFor="conversion-project-type">Type</label>
          <select id="conversion-project-type" className="custom-input__field" name="projectType" value={form.project.projectType} onChange={updateProject}>
            <option value="">Non renseigné</option>
            {AFFAIR_PROJECT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
      </div>;
    }

    if (step === 2) {
      return <div className="affair-conversion-options">
        <fieldset><legend>Lots</legend>{LOTS.map((lot) => <label key={lot}><input type="checkbox" checked={form.lots.includes(lot)} onChange={() => toggle('lots', lot)} /> {lot}</label>)}</fieldset>
        <fieldset><legend>Phases</legend>{PHASES.map((phase) => <label key={phase}><input type="checkbox" checked={Boolean(form.phases[phase])} onChange={() => toggle('phases', phase)} /> {phase}</label>)}</fieldset>
        <fieldset><legend>Gestion</legend>{MANAGEMENT_OPTIONS.map(([key, label]) => <label key={key}><input type="checkbox" checked={Boolean(form.management[key])} onChange={() => toggle('management', key)} /> {label}</label>)}</fieldset>
      </div>;
    }

    if (step === 3) {
      return <div>
        <p className="affair-conversion-help">La structure est désormais définie en amont sur l’Affaire. Elle sera reprise automatiquement pour créer l’arborescence du projet et de la LDA.</p>
        <div className="affair-conversion-summary">
          <div><span>Structure source</span><strong>Affaire</strong></div>
          <div><span>Ouvrages / nœuds</span><strong>{form.nodes.length}</strong></div>
          <div><span>Arborescence</span><strong>{form.nodes.length ? form.nodes.map((node) => node.name).join(', ') : 'Aucun ouvrage spécifique - Général uniquement'}</strong></div>
        </div>
      </div>;
    }

    if (step === 4) {
      return <div className="affair-conversion-options">
        <fieldset>
          <legend>Initialisation</legend>
          <label><input type="checkbox" checked={form.ldaGed.generateLda} onChange={() => toggle('ldaGed', 'generateLda')} /> Générer la LDA initiale</label>
          <p>La création des dossiers Nextcloud est obligatoire. Le projet sera créé uniquement si cette étape réussit.</p>
          {form.ldaGed.planningSource && <p className="affair-conversion-help"><strong>Planning AO/DCE détecté :</strong> {form.ldaGed.planningWorkItemCount} livrable(s), {form.ldaGed.planningEstimatedHours.toFixed(2)} h. Les temps et dates de la LDA seront repris automatiquement depuis ce planning.</p>}
          {form.ldaGed.createGed && <small>La GED sera générée à partir du modèle standard de l’entreprise et des lots/phases sélectionnés. L’arborescence LDA n’en modifie pas la structure.</small>}
        </fieldset>
        {form.ldaGed.generateLda && <fieldset><legend>Types de livrables attendus</legend>{form.ldaGed.planningSource && <small>Ces livrables proviennent du planning AO/DCE. Pour les modifier, ajustez le planning avant la conversion.</small>}{deliverableOptions.map((row) => <label key={row.code}><input type="checkbox" disabled={Boolean(form.ldaGed.planningSource)} checked={Boolean(form.ldaGed.documentTypes[row.code])} onChange={() => toggleDeliverable(row.code)} /> {row.label}</label>)}</fieldset>}
      </div>;
    }

    return <div className="affair-conversion-summary">
      <div><span>Projet</span><strong>{form.project.name}</strong></div>
      <div><span>Partenaire</span><strong>{getAffairPartnerName(affair)}</strong></div>
      <div><span>Devis accepté</span><strong>{acceptedQuote?.quoteNumber || acceptedQuote?.quote_number || '—'}</strong></div>
      <div><span>Montant vendu</span><strong>{formatAffairAmount(acceptedQuote?.totalClientAmount || acceptedQuote?.total_client_amount || acceptedQuote?.totalAmount)}</strong></div>
      <div><span>Lots</span><strong>{form.lots.join(', ') || 'Aucun'}</strong></div>
      <div><span>Phases</span><strong>{selectedPhases.join(', ') || 'Aucune'}</strong></div>
      <div><span>Structure LDA</span><strong>{['Général', ...form.nodes.map((node) => node.name)].join(', ')}</strong></div>
      <div><span>Gestion</span><strong>{MANAGEMENT_OPTIONS.filter(([key]) => form.management[key]).map(([, label]) => label).join(', ') || 'Aucune'}</strong></div>
      <div><span>LDA</span><strong>{form.ldaGed.generateLda ? 'Génération planifiée' : 'Non initialisée'}</strong></div>
      {form.ldaGed.generateLda && <div><span>Livrables</span><strong>{selectedDeliverables.map((row) => row.label).join(', ')}</strong></div>}
      <div><span>GED</span><strong>Création Nextcloud obligatoire avant validation du projet</strong></div>
    </div>;
  };

  return <section className="affair-conversion-wizard">
    <div className="affair-conversion-steps">{STEPS.map((label, index) => <button className={index === step ? 'active' : index < step ? 'complete' : ''} aria-current={index === step ? "step" : undefined} disabled={index > step || converting || Boolean(convertedResult)} key={label} onClick={() => setStep(index)} type="button"><span>{index + 1}</span>{label}</button>)}</div>
    {error && <Alert severity="error">{error}</Alert>}
    <div className="affair-conversion-content"><header className="affair-conversion-step-heading"><span>Étape {step + 1} sur {STEPS.length}</span><h2>{STEPS[step]}</h2></header>{renderStep()}</div>
    <div className="affair-conversion-actions">
      <button className="basic-button" disabled={step === 0 || converting || Boolean(convertedResult)} onClick={() => setStep((current) => current - 1)} type="button">Précédent</button>
      {convertedResult
        ? <button className="basic-button active" onClick={() => onConverted(convertedResult)} type="button">Ouvrir le projet créé</button>
        : step < STEPS.length - 1
          ? <button className="basic-button active" disabled={step === 0 && hasValidationErrors(errors)} onClick={next} type="button">Suivant</button>
          : <button className="basic-button active" disabled={converting || hasValidationErrors(errors)} onClick={convert} type="button">{converting ? 'Conversion...' : 'Créer le projet'}</button>}
    </div>
  </section>;
}

function InterventionConversionWizard({ affair, onConverted }) {
  const [quotes, setQuotes] = useState([]); const [deliverableOptions, setDeliverableOptions] = useState(FALLBACK_DELIVERABLE_OPTIONS); const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false); const [error, setError] = useState('');
  const [form, setForm] = useState({ targetType: 'intervention', acceptedQuoteId: '', primaryContactId: '', lots: [], phases: { EXE: true }, ldaGed: { generateLda: true, documentTypes: {} }, intervention: {
    name: affair.name, description: affair.description || '', priority: affair.priority === 'critical' ? 'urgent' : (affair.priority || 'normal'),
    desiredDueDate: affair.desiredEndDate || '', estimatedHours: '', notes: '', assignedUserId: affair.ownerUserId || '',
  } });
  useEffect(() => { let active = true; Promise.all([getAffairQuotes(affair.id), getAffairContacts(affair.id), getAffairPlanningCatalog(affair.id), getAffairPlanning(affair.id)])
    .then(([quoteRows, contactRows, deliverableCatalog, planning]) => { if (!active) return; setDeliverableOptions((deliverableCatalog?.deliverables || []).filter((row) => row.ldaEnabled !== false)); const accepted = (quoteRows || []).filter((quote) => quote.status === 'accepted'); const primary = (contactRows || []).find((contact) => contact.isPrimary || contact.is_primary); const plannedWork = (planning?.items || []).filter((item) => item.itemType === 'work' && item.source === 'icc' && item.documentTypeCode); const plannedLots = [...new Set(plannedWork.map((item) => item.lotCode).filter(Boolean))]; const plannedDocs = Object.fromEntries([...new Set(plannedWork.map((item) => item.documentTypeCode).filter(Boolean))].map((code) => [code, true])); setQuotes(accepted); setForm((current) => ({ ...current, acceptedQuoteId: accepted[0]?.id || '', primaryContactId: primary?.contactId || primary?.contact_id || '', lots: plannedLots.length ? plannedLots : current.lots, ldaGed: { ...current.ldaGed, generateLda: plannedWork.length ? true : current.ldaGed.generateLda, documentTypes: Object.keys(plannedDocs).length ? plannedDocs : current.ldaGed.documentTypes, planningSource: plannedWork.length > 0 }, intervention: { ...current.intervention, estimatedHours: current.intervention.estimatedHours || (planning?.totalEstimatedHours ? String(planning.totalEstimatedHours) : ''), desiredDueDate: current.intervention.desiredDueDate || planning?.desiredEndDate || '' } })); })
    .catch((e) => active && setError(e.message)).finally(() => active && setLoading(false)); return () => { active = false; }; }, [affair.id]);
  const acceptedQuote = quotes.find((quote) => String(quote.id) === String(form.acceptedQuoteId));
  const errors = validateInterventionConversion(affair, form);
  const update = (event) => setForm((current) => ({ ...current, intervention: { ...current.intervention, [event.target.name]: event.target.value } }));
  const toggleLot = (lot) => setForm((current) => ({ ...current, lots: current.lots.includes(lot) ? current.lots.filter((item) => item !== lot) : [...current.lots, lot] }));
  const togglePhase = (phase) => setForm((current) => ({ ...current, phases: { ...current.phases, [phase]: !current.phases[phase] } }));
  const toggleDeliverable = (key) => setForm((current) => ({ ...current, ldaGed: { ...current.ldaGed, documentTypes: { ...current.ldaGed.documentTypes, [key]: !current.ldaGed.documentTypes[key] } } }));
  const convert = async () => { if (hasValidationErrors(errors)) { setError(Object.values(errors)[0]); return; } try { setConverting(true); setError(''); onConverted(await convertAffair(affair.id, form)); } catch (e) { setError(e.message); } finally { setConverting(false); } };
  if (loading) return <Loader label="Vérification des prérequis..." />;
  return <section className="affair-conversion-wizard affair-conversion-wizard--intervention"><div className="affair-panel__header"><div><h2>Créer la réalisation légère</h2><p>L’intervention conserve un pilotage allégé, mais dispose de la même LDA comme registre de ses livrables.</p></div></div>{error && <Alert severity="error">{error}</Alert>}
    <div className="affair-conversion-checks">{[['Affaire gagnée', affair.stage === 'won'], ['Devis accepté', Boolean(acceptedQuote)], ['Contact principal', Boolean(form.primaryContactId)]].map(([label, ok]) => <div className={ok ? 'ok' : 'ko'} key={label}><span>{ok ? '✓' : '!'}</span>{label}</div>)}</div>
    <div className="affair-conversion-grid"><Input required label="Nom de l’intervention" name="name" value={form.intervention.name} onChange={update} /><Input multiline rows={3} label="Description" name="description" value={form.intervention.description} onChange={update} /><div className="custom-input"><label className="custom-input__label" htmlFor="conversion-priority">Priorité</label><select id="conversion-priority" className="custom-input__field" name="priority" value={form.intervention.priority} onChange={update}><option value="low">Basse</option><option value="normal">Normale</option><option value="high">Haute</option><option value="urgent">Urgente</option></select></div><Input type="date" label="Échéance" name="desiredDueDate" value={form.intervention.desiredDueDate} onChange={update} /><Input type="number" label="Heures estimées" name="estimatedHours" value={form.intervention.estimatedHours} onChange={update} inputProps={{ min: 0 }} /><Input multiline rows={2} label="Notes" name="notes" value={form.intervention.notes} onChange={update} /></div>
    <div className="affair-conversion-options"><fieldset><legend>Lots concernés</legend><div className="affair-chip-grid">{LOTS.map((lot) => <label key={lot}><input type="checkbox" checked={form.lots.includes(lot)} onChange={() => toggleLot(lot)} /> {lot}</label>)}</div>
      </fieldset><fieldset><legend>Phases de réalisation</legend><div className="affair-chip-grid">{PHASES.map((phase) => <label key={phase}><input type="checkbox" checked={Boolean(form.phases[phase])} onChange={() => togglePhase(phase)} /> {phase}</label>)}</div>
      </fieldset><fieldset className="affair-conversion-deliverables"><legend>Livrables initiaux de la LDA</legend><p>La LDA reste le registre des livrables de l’intervention, même si elle n’est pas transmise au client.</p><div className="affair-chip-grid">{deliverableOptions.map((row) => <label key={row.code}><input type="checkbox" checked={Boolean(form.ldaGed.documentTypes[row.code])} onChange={() => toggleDeliverable(row.code)} /> {row.label}</label>)}</div></fieldset>
    </div>
    <div className="affair-conversion-actions"><button className="basic-button active" disabled={converting || hasValidationErrors(errors)} onClick={convert} type="button">{converting ? 'Conversion...' : 'Créer l’intervention'}</button></div></section>;
}

export default function AffairConversionWizard(props) {
  const [targetType, setTargetType] = useState('project');
  return <div className="affair-realization">
    <section className="affair-conversion-target"><h2>Mode de réalisation</h2><p>L’affaire reste le dossier permanent. Choisissez seulement le niveau de pilotage nécessaire à sa réalisation.</p>
      <div className="affair-realization__choices" role="radiogroup" aria-label="Mode de réalisation">
        <label className={targetType === 'project' ? 'is-selected' : ''}><input type="radio" name="realizationType" checked={targetType === 'project'} onChange={() => setTargetType('project')} /><span><strong>Projet - pilotage avancé</strong><small>Organisez les lots, les phases et l’arborescence des livrables.</small></span></label>
        <label className={targetType === 'intervention' ? 'is-selected' : ''}><input type="radio" name="realizationType" checked={targetType === 'intervention'} onChange={() => setTargetType('intervention')} /><span><strong>Intervention - pilotage léger</strong><small>Planifiez une intervention et définissez ses livrables.</small></span></label>
      </div>
    </section>
    {targetType === 'intervention' ? <InterventionConversionWizard {...props} /> : <ProjectConversionWizard {...props} />}
  </div>;
}
